import { Target } from 'lucide-react';
import type { AdminMetricsPayload } from '../../lib/apiClient';

/**
 * **Average Scores** (spec §5).
 *
 * Four radial gauges over every Bottleneck assessment, in production's order,
 * under production's caption. The averages are computed server-side by the same
 * derivation the customer's own result uses, whose equivalence to production's
 * stored columns was validated against a real production CSV row.
 *
 * **The section is drawn only when there are assessments.** Production wraps it
 * in `stats.totalAssessments > 0`, so an empty directory shows no gauges rather
 * than four zeroes. The console owns that condition.
 */
const GAUGES = [
  { key: 'bottleneck', label: 'Bottleneck' },
  { key: 'executionReadiness', label: 'Execution Readiness' },
  { key: 'recoveryDeficit', label: 'Recovery Deficit' },
  { key: 'clarity', label: 'Clarity' },
] as const;

export function AverageScores({
  count,
  scores,
}: {
  count: number;
  scores: AdminMetricsPayload['averageScores'];
}) {
  return (
    <section className="flex flex-col gap-4" data-testid="admin-average-scores">
      <div className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-xl font-bold">
          <Target className="h-5 w-5 text-primary" aria-hidden="true" />
          Average Scores — Bottleneck Assessments
        </h2>
        {/*
          Production pluralises this one caption and nothing else on the page:
          `count === 1 ? "" : "s"`. Reproduced, singular included.
        */}
        <p className="text-sm text-muted-foreground" data-testid="average-scores-caption">
          {`Based on ${count} Bottleneck assessment${count === 1 ? '' : 's'}.`}
        </p>
      </div>

      <div className="grid gap-4 rounded-xl border border-border bg-card p-5 sm:grid-cols-2 lg:grid-cols-4">
        {GAUGES.map((gauge) => (
          <Gauge
            key={gauge.key}
            label={gauge.label}
            value={scores[gauge.key]}
            testId={`gauge-${gauge.key}`}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * One radial gauge. The scores are on a 0–100 scale, so the arc is the value
 * itself; `strokeDasharray` against the circumference draws it without a
 * charting dependency.
 *
 * **The number sits inside the ring**, as production's does, with the label
 * beneath — not stacked below it. The ring is a frame for the figure, and a
 * figure printed under its own ring reads as a caption instead.
 */
function Gauge({ label, value, testId }: { label: string; value: number; testId: string }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const filled = (Math.min(Math.max(value, 0), 100) / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-1" data-testid={testId} data-value={value}>
      <div className="relative h-20 w-20">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" aria-hidden="true">
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            strokeWidth="8"
            className="stroke-secondary"
          />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${filled} ${circumference}`}
            className="stroke-primary"
          />
        </svg>
        <p className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums text-primary">
          {value}
        </p>
      </div>
      <p className="text-center text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
